import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import './BlogsPage.css';
import { useBlogs } from '../hooks/useBlogs';
import { useBlogTopics } from '../hooks/useBlogTopics';
import RichText from '../components/RichText';

const sectionVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 }
};

const sectionTransition = (delay = 0) => ({
  duration: 0.5,
  ease: 'easeOut',
  delay
});

const BlogsPage = () => {
  const navigate = useNavigate();
  const [activeTopic, setActiveTopic] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const blogsPerPage = 6;

  const normalizedTopic = activeTopic === 'all' ? '' : activeTopic;
  const { blogs, allBlogs, isLoading, error } = useBlogs({ topicSlug: normalizedTopic, search: searchQuery });
  const { topics, isLoading: topicsLoading } = useBlogTopics(allBlogs);

  const topicTabs = useMemo(() => [{ slug: 'all', name: 'All Topics' }, ...topics], [topics]);

  const totalPages = Math.max(1, Math.ceil(blogs.length / blogsPerPage));
  const startIndex = (currentPage - 1) * blogsPerPage;
  const displayedBlogs = blogs.slice(startIndex, startIndex + blogsPerPage);

  const handleTopicChange = (topicSlug) => {
    setActiveTopic(topicSlug);
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handleBlogClick = (blogSlug) => {
    if (!blogSlug) return;
    navigate(`/blog/${blogSlug}`);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <main className="blogs-page">
      <motion.section
        className="blogs-header"
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
        transition={sectionTransition(0)}
      >
        <div className="blogs-header-inner">
          <h1 className="blogs-title">Our Blog</h1>
          <p className="blogs-description">
            Insights, stories, and expertise from our team. Stay updated with the latest trends in technology,
            design, and digital innovation. Discover how we help businesses transform and grow.
          </p>
        </div>
      </motion.section>

      <motion.section
        className="blogs-content"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        transition={sectionTransition(0.05)}
      >
        <div className="blogs-container">
          <motion.div
            className="blogs-filters"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            transition={sectionTransition(0.1)}
          >
            {topicTabs.map((topic) => (
              <button
                key={topic.slug}
                className={`filter-btn ${activeTopic === topic.slug ? 'active' : ''}`}
                onClick={() => handleTopicChange(topic.slug)}
                disabled={topicsLoading && topic.slug !== 'all'}
              >
                {topic.name}
              </button>
            ))}
          </motion.div>

          <motion.div
            className="blogs-toolbar"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            transition={sectionTransition(0.15)}
          >
            <div className="blogs-search">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M9.16667 15.8333C12.8485 15.8333 15.8333 12.8485 15.8333 9.16667C15.8333 5.48477 12.8485 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8485 5.48477 15.8333 9.16667 15.8333Z" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M17.5 17.5L13.875 13.875" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <input
                type="text"
                placeholder="Search articles by title, author, or tags"
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>
          </motion.div>

          <motion.div
            className="blogs-grid"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            transition={sectionTransition(0.2)}
          >
            {isLoading ? (
              <div className="blogs-empty-state">
                <h3>Loading blog posts…</h3>
                <p>Please hold on while we fetch the latest articles.</p>
              </div>
            ) : displayedBlogs.length === 0 ? (
              <div className="blogs-empty-state">
                <h3>No posts found</h3>
                <p>Try adjusting your search or switching topics.</p>
              </div>
            ) : (
              displayedBlogs.map((blog) => {
                const badgeItems = (blog.topics && blog.topics.length) ? blog.topics : (blog.tags || []);
                return (
                  <article
                    key={blog.slug || blog.id}
                    className="blog-card"
                    onClick={() => handleBlogClick(blog.slug || blog.id)}
                  >
                  <div className="blog-image-container">
                    <img
                      src={blog.image || 'https://via.placeholder.com/400x250?text=Blog+Image'}
                      alt={blog.title}
                      onError={(event) => {
                        // eslint-disable-next-line no-param-reassign
                        event.currentTarget.src = 'https://via.placeholder.com/400x250?text=Blog+Image';
                      }}
                    />
                    <div className="blog-category-badge">{blog.category}</div>
                  </div>
                  <div className="blog-content">
                    <div className="blog-meta">
                      <span className="blog-author">{blog.author}</span>
                      <span className="blog-date">{formatDate(blog.date)}</span>
                    </div>
                    <RichText as="h2" className="blog-title" content={blog.title} />
                    <RichText as="p" className="blog-excerpt" content={blog.excerpt} />
                    <div className="blog-footer">
                      <div className="blog-tags">
                        {badgeItems.slice(0, 2).map((item, index) => (
                          <span key={typeof item === 'string' ? `${item}-${index}` : `${item.slug}-${index}`} className="blog-tag">
                            {typeof item === 'string' ? item : item.name}
                          </span>
                        ))}
                      </div>
                      <span className="blog-read-time">{blog.readTime}</span>
                    </div>
                    <button className="read-more-btn" type="button">
                      Read More
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                  </article>
                );
              })
            )}
          </motion.div>

          {blogs.length > 0 && totalPages > 1 && (
            <motion.div
              className="blogs-pagination"
              variants={sectionVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              transition={sectionTransition(0.25)}
            >
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M12 15L7 10L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Previous
              </button>

              <div className="pagination-numbers">
                {Array.from({ length: totalPages }).map((_, index) => (
                  <button
                    key={index}
                    className={`pagination-number ${currentPage === index + 1 ? 'active' : ''}`}
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M8 5L13 10L8 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </motion.div>
          )}
          {error && !isLoading && (
            <div className="blogs-status">
              <p>{error}</p>
            </div>
          )}
        </div>
      </motion.section>
    </main>
  );
};

export default BlogsPage;
